import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Eye, Moon, Sparkles, Crown, Compass, Share2, Coins, Users, Trophy, Hash, Cake, Search, Sun, Flame } from 'lucide-react';
import { API } from '../utils/helpers';

const BADGE_ICONS = {
  eye: Eye, search: Search, sparkles: Sparkles, crown: Crown, compass: Compass,
  cake: Cake, moon: Moon, hash: Hash, share: Share2, coins: Coins, users: Users, trophy: Trophy,
};

const LEVEL_ICONS = { seed: Sparkles, sprout: Sparkles, flame: Flame, star: Star, sun: Sun, crown: Crown };

const BadgesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Check for new badges first, then load
    fetch(`${API}/user/check-badges`, { method: 'POST', credentials: 'include' })
      .then(() => fetch(`${API}/user/badges`, { credentials: 'include' }))
      .then(res => res.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="glass-card p-6 mb-8 border border-gold-300/10">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-gold-300 animate-pulse" />
        <span className="text-sm text-[var(--text-muted)]">Cargando logros y nivel...</span>
      </div>
      <div className="flex items-center gap-4 mb-5 p-3 rounded-xl bg-white/5 animate-pulse">
        <div className="w-12 h-12 rounded-full bg-white/5" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-white/5 rounded w-1/3" />
          <div className="h-2 bg-white/5 rounded w-full" />
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
        {[0,1,2,3,4,5].map(i => (
          <div key={i} className="flex flex-col items-center p-2 rounded-xl bg-white/5 animate-pulse">
            <div className="w-10 h-10 rounded-full bg-white/5 mb-1" />
            <div className="h-2 bg-white/5 rounded w-10" />
          </div>
        ))}
      </div>
    </div>
  );
  if (!data) return null;

  const { level, next_level, xp, all_badges, total_unlocked, total_available } = data;
  const LevelIcon = LEVEL_ICONS[level?.icon] || Star;
  const xpProgress = next_level ? ((xp - level.min_xp) / (next_level.min_xp - level.min_xp)) * 100 : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className="glass-card p-6 mb-8 border border-gold-300/10"
      data-testid="badges-widget"
    >
      {/* Level Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold gold-text flex items-center gap-2">
          <Trophy className="w-5 h-5" /> Logros y Nivel
        </h2>
        <span className="text-xs text-[var(--text-muted)]">{total_unlocked}/{total_available} badges</span>
      </div>

      {/* Level Bar */}
      <div className="flex items-center gap-4 mb-5 p-3 rounded-xl bg-white/5">
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-purple-800 shadow-lg shadow-purple-500/20">
          <LevelIcon className="w-6 h-6 text-yellow-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold text-purple-300">Nivel {level?.level}: {level?.name}</span>
            <span className="text-xs text-[var(--text-muted)]">{xp} XP</span>
          </div>
          <div className="h-2 bg-black/40 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-400"
              initial={{ width: 0 }}
              animate={{ width: `${xpProgress}%` }}
              transition={{ duration: 1, delay: 0.3 }}
            />
          </div>
          {next_level && (
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {next_level.min_xp - xp} XP para Nivel {next_level.level}: {next_level.name}
            </p>
          )}
        </div>
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
        {(expanded ? all_badges : all_badges.slice(0, 6)).map((badge, i) => {
          const Icon = BADGE_ICONS[badge.icon] || Star;
          return (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              className={`relative flex flex-col items-center p-2 rounded-xl transition-all cursor-default ${
                badge.unlocked
                  ? 'bg-purple-500/15 border border-gold-300/10 hover:bg-purple-500/25'
                  : 'bg-white/3 border border-white/5 opacity-40'
              }`}
              title={`${badge.name}: ${badge.desc}${badge.unlocked ? ` (+${badge.xp} XP)` : ''}`}
              data-testid={`badge-${badge.id}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                badge.unlocked
                  ? 'bg-gradient-to-br from-yellow-500/30 to-purple-500/30'
                  : 'bg-white/5'
              }`}>
                <Icon className={`w-5 h-5 ${badge.unlocked ? 'text-yellow-400' : 'text-gray-600'}`} />
              </div>
              <span className={`text-xs text-center leading-tight ${badge.unlocked ? 'text-[var(--text-secondary)]' : 'text-gray-600'}`}>
                {badge.name}
              </span>
              {badge.unlocked && (
                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {all_badges.length > 6 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-3 text-xs text-purple-400 hover:text-purple-300 transition-colors"
          data-testid="toggle-badges-btn"
        >
          {expanded ? 'Ver menos' : `Ver todos (${all_badges.length})`}
        </button>
      )}
    </motion.div>
  );
};

export default BadgesWidget;
